import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Progress } from '../../components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Layers,
  TrendingUp,
  AlertTriangle,
  Shield,
  Users,
  DollarSign,
  ArrowUpDown,
  ChevronRight,
  Info,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TRANCHE_COLORS = {
  senior: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-300', bar: 'bg-blue-500' },
  mezzanine: { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-300', bar: 'bg-amber-500' },
  junior: { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-300', bar: 'bg-red-500' }
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value || 0);
};

export default function AdminRiskTranchesPage() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  // State
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({ tranches: [], total_loans: 0, total_principal: 0 });
  const [config, setConfig] = useState({ tranches: [], repayment_order: '' });
  const [deals, setDeals] = useState([]);
  const [investorExposure, setInvestorExposure] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Filters
  const [trancheFilter, setTrancheFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Dialog
  const [showInfoDialog, setShowInfoDialog] = useState(false);
  const [showChangeDialog, setShowChangeDialog] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [newTranche, setNewTranche] = useState('');
  const [updating, setUpdating] = useState(false);

  // Fetch summary
  const fetchSummary = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/lms/risk-tranches/summary`, { headers });
      setSummary(response.data);
    } catch (error) {
      console.error('Error fetching summary:', error);
    }
  }, [token]);

  // Fetch config
  const fetchConfig = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/lms/risk-tranches/config`, { headers });
      setConfig(response.data);
    } catch (error) {
      console.error('Error fetching config:', error);
    }
  }, [token]);

  // Fetch deals
  const fetchDeals = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      params.append('page_size', '100');
      if (trancheFilter && trancheFilter !== 'all') params.append('tranche', trancheFilter);
      if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
      
      const response = await axios.get(`${API_URL}/api/lms/risk-tranches/deals?${params}`, { headers });
      setDeals(response.data.items || []);
    } catch (error) {
      console.error('Error fetching deals:', error);
    }
  }, [token, trancheFilter, statusFilter]);

  // Fetch investor exposure
  const fetchInvestorExposure = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/lms/risk-tranches/investor-exposure`, { headers });
      setInvestorExposure(response.data.investors || []);
    } catch (error) {
      console.error('Error fetching exposure:', error);
    }
  }, [token]);

  // Initial load
  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([
        fetchSummary(),
        fetchConfig(),
        fetchDeals(),
        fetchInvestorExposure()
      ]);
      setLoading(false);
    };
    loadAll();
  }, []);

  // Refetch deals on filter change
  useEffect(() => {
    fetchDeals();
  }, [fetchDeals]);

  // Change tranche
  const handleChangeTranche = async () => {
    if (!selectedDeal || !newTranche) return;
    
    setUpdating(true);
    try {
      await axios.put(
        `${API_URL}/api/lms/deals/${selectedDeal.id}/risk-tranche?tranche=${newTranche}`,
        {},
        { headers }
      );
      toast.success('Risk tranche updated');
      setShowChangeDialog(false);
      fetchSummary();
      fetchDeals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update tranche');
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="tranches-loading">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => <Skeleton key={i} className="h-40" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-risk-tranches-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Layers className="h-6 w-6" />
            Risk Tranche Management
          </h1>
          <p className="text-gray-500 mt-1">
            Monitor and manage loan risk allocation across tranches
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowInfoDialog(true)}>
            <Info className="h-4 w-4 mr-2" /> About Tranches
          </Button>
          <Button variant="outline" onClick={() => {
            fetchSummary();
            fetchDeals();
            fetchInvestorExposure();
            toast.success('Data refreshed');
          }}>
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {summary.tranches.map(tranche => {
          const colors = TRANCHE_COLORS[tranche.tranche] || TRANCHE_COLORS.senior;
          const percentage = summary.total_principal > 0 
            ? (tranche.total_principal / summary.total_principal * 100) 
            : 0;
          
          return (
            <Card key={tranche.tranche} className={`border-l-4 ${colors.border}`}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <Badge className={`${colors.bg} ${colors.text}`}>
                    {tranche.tranche.toUpperCase()}
                  </Badge>
                  <span className="text-sm text-gray-500">
                    Priority {tranche.priority}
                  </span>
                </div>
                <CardTitle className="text-xl mt-2">
                  {formatCurrency(tranche.total_principal)}
                </CardTitle>
                <CardDescription>{tranche.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span>{tranche.total_loans} loans</span>
                    <span>{tranche.avg_interest_rate}% avg rate</span>
                  </div>
                  <Progress value={percentage} className={`h-2 ${colors.bar}`} />
                  <div className="flex gap-2 text-xs">
                    <span className="text-green-600">{tranche.status_breakdown.active} active</span>
                    <span className="text-amber-600">{tranche.status_breakdown.in_arrears} arrears</span>
                    <span className="text-gray-500">{tranche.status_breakdown.settled} settled</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Total Overview */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-8 items-center">
            <div>
              <p className="text-sm text-gray-500">Total Loans</p>
              <p className="text-2xl font-bold">{summary.total_loans}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Principal</p>
              <p className="text-2xl font-bold">{formatCurrency(summary.total_principal)}</p>
            </div>
            <div className="flex-1">
              <p className="text-sm text-gray-500 mb-2">Distribution</p>
              <div className="flex h-4 rounded-full overflow-hidden">
                {summary.tranches.map(t => {
                  const pct = summary.total_principal > 0 
                    ? (t.total_principal / summary.total_principal * 100) 
                    : 0;
                  const colors = TRANCHE_COLORS[t.tranche] || TRANCHE_COLORS.senior;
                  return (
                    <div
                      key={t.tranche}
                      className={colors.bar}
                      style={{ width: `${pct}%` }}
                      title={`${t.tranche}: ${pct.toFixed(1)}%`}
                    />
                  );
                })}
              </div>
              <div className="flex gap-4 mt-1 text-xs">
                {summary.tranches.map(t => {
                  const pct = summary.total_principal > 0 
                    ? (t.total_principal / summary.total_principal * 100) 
                    : 0;
                  return (
                    <span key={t.tranche}>{t.tranche}: {pct.toFixed(1)}%</span>
                  );
                })}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Deals by Tranche</TabsTrigger>
          <TabsTrigger value="investors">Investor Exposure</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4">
          {/* Filters */}
          <Card className="mb-4">
            <CardContent className="p-4">
              <div className="flex gap-4 items-end">
                <div className="w-48">
                  <label className="text-xs text-gray-500">Tranche</label>
                  <Select value={trancheFilter} onValueChange={setTrancheFilter}>
                    <SelectTrigger>
                      <SelectValue placeholder="All Tranches" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Tranches</SelectItem>
                      <SelectItem value="senior">Senior</SelectItem>
                      <SelectItem value="mezzanine">Mezzanine</SelectItem>
                      <SelectItem value="junior">Junior</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-48">
                  <label className="text-xs text-gray-500">Status</label>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger>
                      <SelectValue placeholder="All Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="in_arrears">In Arrears</SelectItem>
                      <SelectItem value="settled">Settled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Deals Table */}
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead>Deal ID</TableHead>
                    <TableHead>Borrower</TableHead>
                    <TableHead>Tranche</TableHead>
                    <TableHead className="text-right">Principal</TableHead>
                    <TableHead className="text-right">Rate</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Investors</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {deals.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                        No deals found
                      </TableCell>
                    </TableRow>
                  ) : (
                    deals.map(deal => {
                      const colors = TRANCHE_COLORS[deal.risk_tranche] || TRANCHE_COLORS.senior;
                      return (
                        <TableRow key={deal.id}>
                          <TableCell className="font-medium">{deal.id?.slice(0, 8)}...</TableCell>
                          <TableCell>{deal.borrower_name || deal.client_name || '-'}</TableCell>
                          <TableCell>
                            <Badge className={`${colors.bg} ${colors.text}`}>
                              {(deal.risk_tranche || 'senior').toUpperCase()}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(deal.principal_amount)}
                          </TableCell>
                          <TableCell className="text-right">{deal.interest_rate}%</TableCell>
                          <TableCell>
                            <Badge variant={
                              deal.status === 'active' ? 'default' :
                              deal.status === 'in_arrears' ? 'destructive' : 'secondary'
                            }>
                              {deal.status}
                            </Badge>
                          </TableCell>
                          <TableCell>{deal.investor_count || 0}</TableCell>
                          <TableCell>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setSelectedDeal(deal);
                                setNewTranche(deal.risk_tranche || 'senior');
                                setShowChangeDialog(true);
                              }}
                            >
                              <ArrowUpDown className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="investors" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="h-5 w-5" />
                Investor Exposure by Tranche
              </CardTitle>
              <CardDescription>
                Shows how each investor's capital is distributed across risk tranches
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead>Investor</TableHead>
                    <TableHead className="text-right">Total Outstanding</TableHead>
                    <TableHead className="text-center">
                      <Badge className={`${TRANCHE_COLORS.senior.bg} ${TRANCHE_COLORS.senior.text}`}>Senior</Badge>
                    </TableHead>
                    <TableHead className="text-center">
                      <Badge className={`${TRANCHE_COLORS.mezzanine.bg} ${TRANCHE_COLORS.mezzanine.text}`}>Mezzanine</Badge>
                    </TableHead>
                    <TableHead className="text-center">
                      <Badge className={`${TRANCHE_COLORS.junior.bg} ${TRANCHE_COLORS.junior.text}`}>Junior</Badge>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {investorExposure.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                        No investor exposure data
                      </TableCell>
                    </TableRow>
                  ) : (
                    investorExposure.map(inv => (
                      <TableRow key={inv.investor_id}>
                        <TableCell className="font-medium">{inv.investor_name}</TableCell>
                        <TableCell className="text-right font-bold">
                          {formatCurrency(inv.total_outstanding)}
                        </TableCell>
                        <TableCell className="text-center">
                          {inv.tranche_breakdown.senior ? (
                            <div>
                              <div className="font-medium">{formatCurrency(inv.tranche_breakdown.senior.outstanding)}</div>
                              <div className="text-xs text-gray-500">{inv.tranche_breakdown.senior.percentage}%</div>
                            </div>
                          ) : '-'}
                        </TableCell>
                        <TableCell className="text-center">
                          {inv.tranche_breakdown.mezzanine ? (
                            <div>
                              <div className="font-medium">{formatCurrency(inv.tranche_breakdown.mezzanine.outstanding)}</div>
                              <div className="text-xs text-gray-500">{inv.tranche_breakdown.mezzanine.percentage}%</div>
                            </div>
                          ) : '-'}
                        </TableCell>
                        <TableCell className="text-center">
                          {inv.tranche_breakdown.junior ? (
                            <div>
                              <div className="font-medium">{formatCurrency(inv.tranche_breakdown.junior.outstanding)}</div>
                              <div className="text-xs text-gray-500">{inv.tranche_breakdown.junior.percentage}%</div>
                            </div>
                          ) : '-'}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Info Dialog */}
      <Dialog open={showInfoDialog} onOpenChange={setShowInfoDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Understanding Risk Tranches
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <p className="text-gray-600">{config.repayment_order}</p>
            
            {config.tranches.map(t => {
              const colors = TRANCHE_COLORS[t.name] || TRANCHE_COLORS.senior;
              return (
                <div key={t.name} className={`p-4 rounded-lg border ${colors.border} ${colors.bg}`}>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className={`font-bold ${colors.text}`}>{t.display_name}</h4>
                    <span className="text-sm">Priority {t.priority} • {t.typical_rate_range}</span>
                  </div>
                  <p className="text-sm">{t.description}</p>
                </div>
              );
            })}
          </div>

          <DialogFooter>
            <Button onClick={() => setShowInfoDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Tranche Dialog */}
      <Dialog open={showChangeDialog} onOpenChange={setShowChangeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Risk Tranche</DialogTitle>
            <DialogDescription>
              Update the risk tranche for deal: {selectedDeal?.id?.slice(0, 8)}...
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div>
              <p className="text-sm text-gray-500">Current Tranche</p>
              <Badge className={`${TRANCHE_COLORS[selectedDeal?.risk_tranche || 'senior'].bg} ${TRANCHE_COLORS[selectedDeal?.risk_tranche || 'senior'].text}`}>
                {(selectedDeal?.risk_tranche || 'senior').toUpperCase()}
              </Badge>
            </div>
            
            <div>
              <label className="text-sm font-medium">New Tranche</label>
              <Select value={newTranche} onValueChange={setNewTranche}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="senior">Senior (Priority 1)</SelectItem>
                  <SelectItem value="mezzanine">Mezzanine (Priority 2)</SelectItem>
                  <SelectItem value="junior">Junior (Priority 3)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">
                <AlertTriangle className="h-4 w-4 inline mr-1" />
                Changing the risk tranche affects how this loan is prioritized in case of default.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowChangeDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleChangeTranche} disabled={updating}>
              {updating ? 'Updating...' : 'Update Tranche'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
